import React, { useEffect, useState } from "react";
import "./OwAccordion.scss";
import TitleTagEnum from "../../../models/TitleTagEnum";
import OwTitle from "../OwTitle/OwTitle";

type Props = {
    /**
     * Allow the user to toggle the accordion open with the entire width being clickable.
     */
    fullWidthClick?: boolean;
    /**
     * Allow the user to toggle the accordion open with a title click
     */
    titleClick?: boolean;
    /**
     * Place an icon on the left side of the accordion
     */
    leftIcon?: string;
    /**
     * Place an image on the left side of the accordion
     */
    leftImage?: string;
    /**
     * An alt text for the left image
     */
    leftImageAlt?: string;
    /**
     * Specify the height of the left image
     */
    leftImageHeight?: string | number;
    /**
     * Add an element to the left hand side
     */
    leftElem?: any;
    /**
     * The title text for the accordion
     */
    title?: string;
    /**
     * The title tag type - H1, H2, etc.
     */
    titleTag?: TitleTagEnum;
    /**
     * The name for the accordion. Required for a series if you only want to allow one open at a time.
     */
    name?: string;
    /**
     * Add an element to the right hand side.
     */
    rightElem?: any;
    /**
     * Hide the Chevron from the user
     */
    hideChevron?: boolean;
    /**
     * React 18 Fix
     */
    children?: any;
    /**
     * Function called when opened.
     */
    fn?: () => void;
    /**
     * Function called when clicked.
     */
    clickFn?: () => void;
    /**
     * Specify if the accordion should be open on page load.
     */
    initOpen?: boolean;
    /**
     * Chevron side placement
     */
    chevronSide?: "LEFT" | "RIGHT";
    /**
     * Force if the accordion should be open or not
     */
    openOverride?: boolean;
    className?: string;
    /**
     * Skip/Disable the title translation
     */
    disableTranslation?: boolean;
};

/**
 * OwAccordion
 *
 * Creates an accordion that shows the children elements in the body container when open.
 *
 * @param props
 * @constructor
 */
export const OwAccordion: React.FC<Props> = (props) => {
    const [isOpen, setIsOpen] = useState(props.initOpen || false);

    useEffect(() => {
        if (props.hideChevron) {
            setIsOpen(false);
        }
    }, [props.hideChevron]);

    useEffect(() => {
        if (isOpen) {
            if (props.fn) {
                props.fn();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (props.openOverride !== undefined) {
            setIsOpen(props.openOverride);
        }
    }, [props.openOverride]);

    const toggleOpen = () => {
        setIsOpen(!isOpen);

        if (props.clickFn) {
            props.clickFn();
        }
    };

    const handleHeaderClick = () => {
        if (props.fullWidthClick) {
            toggleOpen();

            if (props.clickFn) {
                props.clickFn();
            }
        }
    };

    const handleTitleClick = () => {
        if (props.titleClick) {
            toggleOpen();

            if (props.clickFn) {
                props.clickFn();
            }
        }
    };

    const chevronElem = props.hideChevron ? (
        <i className={"chevron"} />
    ) : (
        <i className={"chevron pointer " + (isOpen ? "fas fa-chevron-down" : "fas fa-chevron-right")} onClick={toggleOpen} />
    );

    return (
        <div className={"OwAccordion " + props.className}>
            <div
                className={
                    (props.fullWidthClick ? "pointer" : "") +
                    " header-container " +
                    (isOpen ? "is-open" : "") +
                    (props.chevronSide === "RIGHT" ? " justify-content-between " : "")
                }
                onClick={handleHeaderClick}>
                {props.chevronSide === "RIGHT" ? null : chevronElem}
                <div className={(props.titleClick ? "pointer" : "") + " d-flex align-items-center "} onClick={handleTitleClick}>
                    {props.leftIcon ? <i className={"ms-2 " + props.leftIcon} /> : null}
                    {props.leftImage ? <img src={props.leftImage} alt={props.leftImageAlt} height={props.leftImageHeight} /> : null}
                    {props.title ? (
                        <OwTitle
                            title={props.title}
                            tag={props.titleTag}
                            defaultMargins={false}
                            className={"d-inline-flex"}
                            disableTranslation={props.disableTranslation}
                        />
                    ) : null}
                    {props.leftElem ? props.leftElem : null}
                </div>
                {props.rightElem ? props.rightElem : null}
                {props.chevronSide === "RIGHT" ? chevronElem : null}
            </div>
            <div className={"body-container " + (isOpen ? "" : "d-none")}>{props.children}</div>
        </div>
    );
};
